import React from "react";
import { connect } from "react-redux";
import ReactDOM from "react-dom";
import glamorous from "glamorous";
import { Overlay } from "react-bootstrap";
import Select from "react-select";
import "react-select/dist/react-select.css";
import DateTimePicker from "react-widgets/lib/DateTimePicker";
import "react-widgets/dist/css/react-widgets.css";
import moment from "moment";
import momentLocalizer from "react-widgets-moment";

import SearchState from "./ShipmentSearchState";
import Colors from "../../styles/colors";
import Close from "react-icons/lib/md/close";

const MAX_FILTER_LABEL_LENGTH = 20;

const ButtonWrapper = glamorous.div(
  {
    display: "flex",
    overflow: "hidden",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: "1em",
    height: "2em",
    marginTop: "0.5em",
    marginBottom: "0.5em",
    marginRight: "0.5em",
    border: `1px solid ${Colors.background.DARK_BLUE}`
  },
  ({ toggled = false, hasValue = false }) => ({
    backgroundColor: hasValue
      ? "white"
      : toggled
        ? Colors.background.GRAY
        : Colors.background.LIGHT_GRAY,
    color: Colors.background.DARK_BLUE,
    ":hover": {
      cursor: "pointer",
      backgroundColor: Colors.background.GRAY
    }
  })
);

const FilterPopover = props => {
  const { onToggle, filterKey } = props;

  return (
    <glamorous.Div
      css={{
        ...props.style,
        zIndex: 100,
        position: "absolute",
        boxShadow: "0px 4px 5px rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
        backgroundColor: Colors.background.LIGHT_GRAY,
        marginTop: "0.75em"
      }}
    >
      <glamorous.Div
        css={{
          display: "flex",
          color: "white",
          backgroundColor: Colors.background.DARK_BLUE,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.5em",
          fontSize: "1.2em",
          fontWeight: "300"
        }}
      >
        {props.label}
        <glamorous.Div
          css={{ ":hover": { cursor: "pointer" } }}
          onClick={() => onToggle(filterKey)}
        >
          <Close width="1.25em" height="1.25em" />
        </glamorous.Div>
      </glamorous.Div>
      <glamorous.Div css={{ margin: "1em" }}>{props.children}</glamorous.Div>
    </glamorous.Div>
  );
};

class FilterButton extends React.Component {
  componentWillMount() {
    moment.locale("en");
    momentLocalizer();
  }

  render() {
    const {
      filterKey,
      filterValue,
      label,
      toggled,
      onToggle,
      clearSearchFilter
    } = this.props;

    // Truncate filter value if it's long
    let filterLabel = filterValue;
    if (filterValue && filterValue.length > MAX_FILTER_LABEL_LENGTH) {
      filterLabel = `${filterLabel.slice(0, MAX_FILTER_LABEL_LENGTH)}…`;
    }

    return (
      <glamorous.Div css={{ position: "relative" }}>
        <glamorous.Div
          css={{ position: "relative" }}
          ref={d => {
            this.container = d;
          }}
        >
          <ButtonWrapper
            ref={button => {
              this.target = button;
            }}
            toggled={toggled}
            hasValue={filterValue != null}
            onClick={() => onToggle(filterKey)}
          >
            <glamorous.Div css={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
              {label}
            </glamorous.Div>
            {filterValue == null && (
              <glamorous.Div
                css={{ marginLeft: "0.5em", marginRight: "0.5em" }}
              >
                {"▾"}
              </glamorous.Div>
            )}
            {filterValue != null && (
              <glamorous.Div
                css={{
                  paddingLeft: "0.5em",
                  paddingRight: "0.5em",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  backgroundColor: Colors.background.GRAY_BLUE,
                  color: "white"
                }}
              >
                {filterLabel}
                <glamorous.Div
                  onClick={e => {
                    clearSearchFilter(filterKey);
                    e.stopPropagation();
                  }}
                  css={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "white",
                    color: Colors.background.DARK_BLUE,
                    marginLeft: "5px",
                    borderRadius: "1em",
                    width: "1em",
                    height: "1em",
                    ":hover": {
                      backgroundColor: Colors.highlight.RED
                    }
                  }}
                >
                  <Close />
                </glamorous.Div>
              </glamorous.Div>
            )}
          </ButtonWrapper>
        </glamorous.Div>
        <Overlay
          show={toggled}
          placement="bottom"
          container={() => ReactDOM.findDOMNode(this.container)}
          target={() => ReactDOM.findDOMNode(this.target)}
          animation={false}
        >
          <FilterPopover {...this.props}>{this.props.children}</FilterPopover>
        </Overlay>
      </glamorous.Div>
    );
  }
}

const SelectFilterButtonBase = props => {
  const {
    filterKey,
    options,
    searchFilters,
    setSearchFilter,
    clearSearchFilter
  } = props;
  const filterValue = searchFilters[filterKey]
    ? options.find(o => o.value === searchFilters[filterKey]).label
    : null;

  return (
    <FilterButton
      {...props}
      filterValue={filterValue}
      clearSearchFilter={clearSearchFilter}
    >
      <Select
        style={{ minWidth: "17em" }}
        options={options}
        simpleValue
        name="select-filter"
        value={searchFilters[filterKey]}
        onChange={v => setSearchFilter(filterKey, v)}
      />
    </FilterButton>
  );
};

const DateRangeFilterButtonBase = props => {
  const {
    filterKey,
    searchFilters,
    setSearchFilter,
    clearSearchFilter
  } = props;
  const currentFilter = searchFilters[filterKey];
  const fromValue = currentFilter ? currentFilter.from : null;
  const toValue = currentFilter ? currentFilter.to : null;

  let filterLabel = null;
  if (fromValue && !toValue) {
    filterLabel = (
      <div>
        <strong> From: {moment(fromValue).format("l")} </strong>
        {moment(fromValue).format(" HH:mm")}
      </div>
    );
  }
  if (toValue && !fromValue) {
    filterLabel = (
      <div>
        <strong> To: {moment(toValue).format("l")} </strong>
        {moment(toValue).format(" HH:mm")}
      </div>
    );
  }
  if (toValue && fromValue) {
    filterLabel = (
      <div>
        <strong> {moment(fromValue).format("l")} </strong>
        {moment(fromValue).format(" HH:mm")} -
        <strong> {moment(toValue).format("l")} </strong>
        {moment(toValue).format(" HH:mm")}
      </div>
    );
  }

  return (
    <FilterButton
      {...props}
      clearSearchFilter={clearSearchFilter}
      filterValue={filterLabel}
    >
      <glamorous.Div css={{ marginBottom: "1em" }}>
        <strong className="timeLabel">From:</strong>
        <DateTimePicker
          style={{ minWidth: "17em" }}
          defaultValue={fromValue}
          onChange={date =>
            setSearchFilter(filterKey, { from: date, to: toValue })
          }
        />
      </glamorous.Div>
      <div>
        <strong className="timeLabel">To:</strong>
        <DateTimePicker
          style={{ minWidth: "17em" }}
          defaultValue={toValue}
          onChange={date =>
            setSearchFilter(filterKey, { from: fromValue, to: date })
          }
        />
      </div>
    </FilterButton>
  );
};

function mapStateToProps(state) {
  return {
    searchFilters: SearchState.selectors.getSearchFilters(state)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setSearchFilter: (key, value) =>
      dispatch(SearchState.actionCreators.setSearchFilter(key, value)),
    clearSearchFilter: key =>
      dispatch(SearchState.actionCreators.clearSearchFilter(key))
  };
}

const SelectFilterButton = connect(
  mapStateToProps,
  mapDispatchToProps
)(SelectFilterButtonBase);
const DateRangeFilterButton = connect(
  mapStateToProps,
  mapDispatchToProps
)(DateRangeFilterButtonBase);

export { SelectFilterButton, DateRangeFilterButton };



// WEBPACK FOOTER //
// ./src/modules/shipment-search/FilterButtons.js